/**
 * Project drafts — server-persisted, resumable in-progress project
 * creations/requests. `payload` is the screen's full serialized intake state
 * (opaque); `hints` is the small denormalized set the list + AI naming read;
 * `title` is the AI-standardized name.
 */
import { z } from 'zod';
import { IsoDateTimeSchema, UuidSchema } from './common.js';
export const ProjectDraftSourceSchema = z.enum(['chat', 'form']);
/** Where a draft's `title` came from — gates auto-renaming.
 *  'fallback' keep retrying as content arrives · 'ai' settled, re-name only when
 *  inputs change · 'user' renamed inline, never auto-overwrite. */
export const ProjectDraftTitleSourceSchema = z.enum(['fallback', 'ai', 'user']);
/** Denormalized fields the draft list shows + the AI name is built from — so the
 *  list never has to parse the (potentially large) payload. */
export const ProjectDraftHintsSchema = z.object({
    project_title: z.string().max(300).nullish(),
    kind: z.string().max(50).nullish(),
    client: z.string().max(300).nullish(),
    /** ISO datetime or yyyy-MM-dd. */
    deadline: z.string().max(40).nullish(),
    /** Chat: number of user turns. */
    item_count: z.number().int().min(0).nullish(),
    /** Chat has been "built" into an extracted draft (form + chat both present). */
    built: z.boolean().nullish(),
});
/** Opaque serialized intake state, bounded so a runaway draft can't bloat the row
 *  (chat history strips inline file bytes client-side before saving). */
export const ProjectDraftPayloadSchema = z
    .record(z.string(), z.unknown())
    .refine((v) => JSON.stringify(v).length <= 2_000_000, {
    message: 'Draft is too large to save.',
});
/** List row — no payload. */
export const ProjectDraftSchema = z.object({
    id: UuidSchema,
    source: ProjectDraftSourceSchema,
    title: z.string().nullable(),
    title_source: ProjectDraftTitleSourceSchema,
    hints: ProjectDraftHintsSchema,
    created_at: IsoDateTimeSchema,
    updated_at: IsoDateTimeSchema,
});
/** Full draft, with the payload — used to resume. */
export const ProjectDraftFullSchema = ProjectDraftSchema.extend({
    payload: z.record(z.string(), z.unknown()),
});
export const ProjectDraftSaveSchema = z.object({
    source: ProjectDraftSourceSchema,
    payload: ProjectDraftPayloadSchema,
    hints: ProjectDraftHintsSchema.default({}),
});
export const ProjectDraftUpdateSchema = z.object({
    payload: ProjectDraftPayloadSchema,
    hints: ProjectDraftHintsSchema.default({}),
});
/** Inline rename from the drafts list — user-authored, locks the title. */
export const ProjectDraftRenameSchema = z.object({
    title: z.string().trim().min(1).max(120),
});
export const ProjectDraftListResponseSchema = z.object({
    data: z.array(ProjectDraftSchema),
});
//# sourceMappingURL=project-drafts.js.map